/**
 * GA4 analytics helper for SUX1.
 *
 * - Consent Mode v2 is bootstrapped in `public/index.html` (default = denied).
 * - This module exposes helpers to update consent and to fire custom events.
 * - All calls are safe no-ops if gtag is missing (offline / adblock / SSR).
 */

const GA_ID = "G-T4QR3VTMDM";
const CONSENT_KEY = "sux1_consent"; // 'granted' | 'denied' | null

const hasGtag = () => typeof window !== "undefined" && typeof window.gtag === "function";

export const getConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch (_) {
    return null;
  }
};

export const setConsent = (value /* 'granted' | 'denied' */) => {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch (_) {
    /* ignore */
  }
  if (!hasGtag()) return;
  window.gtag("consent", "update", {
    analytics_storage: value === "granted" ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
};

/** Track a custom event. `params` can carry any GA4-compatible key. */
export const trackEvent = (name, params = {}) => {
  if (!hasGtag()) return;
  try {
    window.gtag("event", name, params);
  } catch (_) {
    /* ignore */
  }
};

/** Manual SPA page_view (called on route changes). */
export const trackPageview = (path, title) => {
  if (!hasGtag()) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
      send_to: GA_ID,
    });
  } catch (_) {
    /* ignore */
  }
};

// ---------------- SUX1-specific event helpers ----------------

export const trackSignUp = (method = "email") =>
  trackEvent("sign_up", { method });

export const trackSummaryGenerated = ({ model, format, length, register, source_type, chars }) =>
  trackEvent("summary_generated", {
    model,
    format,
    length,
    register,
    source_type,
    summary_chars: chars,
  });

export const trackPaywallShown = (context = "generate") => {
  logFunnel("paywall_view");
  trackEvent("paywall_shown", { context });
};

export const trackCheckoutStarted = () => {
  logFunnel("checkout_start");
  trackEvent("begin_checkout", { currency: "EUR", value: 2.0 });
};

export const trackPurchase = (transactionId) =>
  trackEvent("purchase", {
    transaction_id: transactionId || `sux1-${Date.now()}`,
    value: 2.0,
    currency: "EUR",
    items: [{ item_id: "pro_lifetime", item_name: "SUX1 Pro Lifetime", price: 2.0, quantity: 1 }],
  });

export const trackDemoViewed = () => trackEvent("demo_page_viewed");

export const trackShare = (method /* 'gmail' | 'print' | 'copy' | 'native' */) =>
  trackEvent("share", { method, content_type: "summary" });

// Server-side weekly funnel (admin "Où les gens s'arrêtent"), deduplicated per visitor/week.
const visitorKey = () => {
  try {
    let k = localStorage.getItem("sux1_visitor");
    if (!k) { k = (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/[^A-Za-z0-9-]/g, ""); localStorage.setItem("sux1_visitor", k); }
    return k;
  } catch { return null; }
};

export const logFunnel = (name) => {
  const key = visitorKey();
  if (!key) return;
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/funnel/event`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, key }), keepalive: true,
  }).catch(() => {});
};
